import { Download, ExternalLink, MapPin, Calendar } from 'lucide-react';
import { getFileIcon } from '../../utils/fileUtils';
import { IscBadge } from '../badges';
import { BRAND_TEXT } from './constants';
import { ResultSelectHover, ResultSelectionControl } from './ResultCheckbox';
import { rowClickTogglesSelection, type ResultSelectionControls } from './resultSelection';
import { formatDate, getSearchResultGeoLines } from './searchFormat';
import type { SearchResult } from './types';

/** First address line a result has, plus how many more there are, for a one-line summary. */
function summarizeAddress(result: SearchResult): string | null {
  const [first, ...rest] = getSearchResultGeoLines(result);
  if (!first) return null;
  return rest.length > 0 ? `${first} (+${rest.length} more)` : first;
}

/** Compact, single-row rendering of a search result — title and ISC classification at a glance, trading summary/preview/other badges for density. */
const ResultRow = ({
  result,
  onDownload,
  navigate,
  selection,
}: {
  result: SearchResult;
  onDownload: (id: string) => void;
  navigate: (path: string) => void;
  selection: ResultSelectionControls;
}) => {
  const informationSecurityClassification = result.information_security_classification?.level || 'unclassified';
  const createdAt = result.metadata?.created;
  const contentType = result.file?.content_type || '';
  const address = summarizeAddress(result);
  const selected = selection.isSelected(result.id);

  return (
    <div
      className={`group relative flex items-center gap-3 bg-white rounded-lg border px-4 py-2.5 hover:shadow-sm hover:border-emerald-200 transition-all duration-200 ${
        selected ? 'border-emerald-300 bg-emerald-50/60' : 'border-gray-100'
      } ${selection.active ? 'cursor-pointer' : ''}`}
      onClick={(event) => {
        if (!selection.enabled || !selection.active || !rowClickTogglesSelection(event.target)) return;
        selection.onToggle(result.id, event.shiftKey);
      }}
    >
      <ResultSelectionControl
        selection={selection}
        recordId={result.id}
        title={result.title}
      />
      <div className="shrink-0">{getFileIcon(contentType, 'w-5 h-5')}</div>

      <div className="min-w-0 flex-1">
        <a
          href={`/records/${result.id}`}
          onClick={(e) => { if (!e.ctrlKey && !e.metaKey) { e.preventDefault(); navigate(`/records/${result.id}`); } }}
          className={`text-sm font-semibold ${BRAND_TEXT} hover:underline truncate block text-left`}
          title={result.title}
        >
          {result.title}
        </a>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400 truncate">
          {address && (
            <span className="flex items-center gap-1 truncate">
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate">{address}</span>
            </span>
          )}
          {createdAt && (
            <span className="flex items-center gap-1 shrink-0">
              <Calendar className="w-3 h-3" />
              {formatDate(createdAt)}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <ResultSelectHover
          selection={selection}
          recordId={result.id}
          title={result.title}
          className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
        />
        <button
          onClick={() => onDownload(result.id)}
          className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
          title="Download"
        >
          <Download className="w-4 h-4" />
        </button>
        <a
          href={`/records/${result.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
          title="View details"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      <IscBadge level={informationSecurityClassification} className="shrink-0" />
    </div>
  );
};

export default ResultRow;
